import {
  lazy,
  Suspense,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import { Banner, Button, Icon, TextField } from '../components';
import { formatCoordinates, mapsHref, parseCoordinates, type MapPin, type MapPlace } from './map';

export type { MapPin, MapPlace } from './map';

// mapbox-gl loads when the map first shows, so it stays out of every other page.
const MapCanvas = lazy(() => import('./MapCanvas'));

const MIN_QUERY = 3;
const DEBOUNCE_MS = 300;

type Search =
  | { state: 'idle' }
  | { state: 'searching'; query: string }
  | { state: 'done'; query: string; places: MapPlace[] }
  | { state: 'failed'; query: string; reason: string };

type Option = { kind: 'pin'; pin: MapPin } | { kind: 'place'; place: MapPlace };

const errorText = (error: unknown) =>
  error instanceof Error ? error.message : 'The map service gave no reason.';

/**
 * A pin on a map: search for a place, paste coordinates, or click and drag on the map. The
 * caller does the searching and address lookups, so the picker knows no map service's API.
 */
export function MapPicker({
  label,
  hint,
  error,
  pin,
  onPin,
  onPlace,
  accessToken,
  search,
  lookup,
  optional,
  disabled,
}: {
  label: string;
  optional?: boolean;
  hint?: ReactNode;
  error?: ReactNode;
  pin: MapPin | null;
  /** The pin moved or was removed. */
  onPin: (pin: MapPin | null) => void;
  /** The place at the pin: chosen from a search, or looked up after the pin moved. */
  onPlace?: (place: MapPlace, chosen: boolean) => void;
  /** Without one there's no map, and pins come from typed coordinates only. */
  accessToken?: string;
  search?: (query: string, near: MapPin | null, signal: AbortSignal) => Promise<MapPlace[]>;
  lookup?: (
    pin: MapPin,
    signal: AbortSignal,
  ) => Promise<{ address: string; placeId?: string } | null>;
  disabled?: boolean;
}) {
  const id = useId();
  const [query, setQuery] = useState('');
  const [found, setFound] = useState<Search>({ state: 'idle' });
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const [focusKey, setFocusKey] = useState(0);
  const [mapFailed, setMapFailed] = useState<string | null>(null);
  const [lookingUp, setLookingUp] = useState(false);
  const searchAbort = useRef<AbortController | null>(null);
  const lookupAbort = useRef<AbortController | null>(null);
  const shown = useRef(pin);
  const withMap = !!accessToken;

  useEffect(() => {
    shown.current = pin;
  }, [pin]);

  useEffect(
    () => () => {
      searchAbort.current?.abort();
      lookupAbort.current?.abort();
    },
    [],
  );

  const typed = parseCoordinates(query);
  const isCoordinates = !!typed;
  const trimmed = query.trim();
  const places = found.state === 'done' && found.query === trimmed ? found.places : [];
  const options: Option[] = [
    ...(typed ? [{ kind: 'pin' as const, pin: typed }] : []),
    ...places.map((place) => ({ kind: 'place' as const, place })),
  ];
  const listOpen = open && options.length > 0;

  useEffect(() => {
    if (!search || isCoordinates || trimmed.length < MIN_QUERY) {
      searchAbort.current?.abort();
      return;
    }
    const abort = new AbortController();
    const timer = window.setTimeout(() => {
      searchAbort.current?.abort();
      searchAbort.current = abort;
      setFound({ state: 'searching', query: trimmed });
      search(trimmed, shown.current, abort.signal).then(
        (result) =>
          !abort.signal.aborted && setFound({ state: 'done', query: trimmed, places: result }),
        (reason) =>
          !abort.signal.aborted &&
          setFound({ state: 'failed', query: trimmed, reason: errorText(reason) }),
      );
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [search, trimmed, isCoordinates]);

  const lookUp = (next: MapPin) => {
    lookupAbort.current?.abort();
    if (!lookup) return;
    const abort = new AbortController();
    lookupAbort.current = abort;
    setLookingUp(true);
    lookup(next, abort.signal)
      .then((place) => {
        const current = shown.current;
        const still = current?.latitude === next.latitude && current?.longitude === next.longitude;
        if (place && still && !abort.signal.aborted) {
          onPlace?.({ ...next, name: place.address, ...place }, false);
        }
      })
      .catch(() => undefined)
      .finally(() => !abort.signal.aborted && setLookingUp(false));
  };

  const place = (next: MapPin) => {
    shown.current = next;
    onPin(next);
    lookUp(next);
  };

  const choose = (option: Option) => {
    setQuery('');
    setOpen(false);
    setActive(-1);
    setFocusKey((n) => n + 1);
    if (option.kind === 'pin') {
      place(option.pin);
      return;
    }
    lookupAbort.current?.abort();
    setLookingUp(false);
    const next = { latitude: option.place.latitude, longitude: option.place.longitude };
    shown.current = next;
    onPin(next);
    onPlace?.(option.place, true);
  };

  const remove = () => {
    lookupAbort.current?.abort();
    setLookingUp(false);
    shown.current = null;
    onPin(null);
  };

  const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      // Enter picks from the list; it never submits the form around the picker.
      event.preventDefault();
      const option = options[active] ?? (listOpen ? options[0] : undefined);
      if (option) choose(option);
      return;
    }
    if (event.key === 'Escape' && listOpen) {
      event.preventDefault();
      event.stopPropagation();
      setOpen(false);
      setActive(-1);
      return;
    }
    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    event.preventDefault();
    if (!listOpen) {
      setOpen(true);
      return;
    }
    const last = options.length - 1;
    const step = event.key === 'ArrowDown' ? 1 : -1;
    setActive((i) => (i < 0 ? (step > 0 ? 0 : last) : Math.min(last, Math.max(0, i + step))));
  };

  const optionId = (i: number) => `${id}-opt-${i}`;
  const searching = found.state === 'searching' && found.query === trimmed;
  const failed = found.state === 'failed' && found.query === trimmed ? found.reason : null;
  const nothing =
    found.state === 'done' && found.query === trimmed && !places.length && !typed && open;

  return (
    <div
      className={error ? 'rq-field rq-map is-invalid' : 'rq-field rq-map'}
      role="group"
      aria-labelledby={`${id}-label`}
    >
      <span className="rq-field-label" id={`${id}-label`}>
        {label}
        {optional ? <span className="rq-field-opt"> (optional)</span> : null}
      </span>

      {disabled ? null : (
        <div className="rq-map-search">
          <div className="rq-map-combo">
            <TextField
              label={withMap ? 'Find the place' : 'Coordinates'}
              hideLabel
              icon="search"
              value={query}
              disabled={disabled}
              placeholder={
                withMap
                  ? 'Search, or paste coordinates'
                  : 'Paste coordinates, such as 44.81250, 20.46120'
              }
              autoComplete="off"
              spellCheck={false}
              lang="sr"
              role="combobox"
              aria-autocomplete="list"
              aria-expanded={listOpen}
              aria-controls={`${id}-list`}
              aria-activedescendant={listOpen && active >= 0 ? optionId(active) : undefined}
              onChange={(e: { target: { value: string } }) => {
                setQuery(e.target.value);
                setOpen(true);
                setActive(-1);
              }}
              onFocus={() => setOpen(true)}
              onBlur={() => setOpen(false)}
              onKeyDown={onKeyDown}
            />
            {listOpen ? (
              <ul className="rq-map-options" id={`${id}-list`} role="listbox" aria-label="Places">
                {options.map((option, i) => (
                  <li
                    key={option.kind === 'pin' ? 'pin' : `${option.place.placeId}-${i}`}
                    id={optionId(i)}
                    role="option"
                    aria-selected={i === active}
                    className={i === active ? 'rq-map-option is-active' : 'rq-map-option'}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      choose(option);
                    }}
                  >
                    <Icon name="map-pin" size={14} />
                    {option.kind === 'pin' ? (
                      <span className="rq-map-option-text">
                        <span className="rq-map-option-name">
                          Pin at {formatCoordinates(option.pin)}
                        </span>
                        <span className="rq-map-option-meta">The coordinates you typed</span>
                      </span>
                    ) : (
                      <span className="rq-map-option-text" lang="sr">
                        <span className="rq-map-option-name">{option.place.name}</span>
                        <span className="rq-map-option-meta">{option.place.address}</span>
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
          <p className="rq-map-status" aria-live="polite">
            {searching ? (
              <>
                <Icon name="loader-circle" size={14} className="rq-spin" />
                Searching…
              </>
            ) : failed ? (
              <>
                <Icon name="triangle-alert" size={14} className="rq-map-caution" />
                Couldn't search for places. Reason given: {failed}
              </>
            ) : nothing ? (
              <>No places match “{trimmed}”. Try the street or the town, or click the map.</>
            ) : null}
          </p>
        </div>
      )}

      {withMap && mapFailed ? (
        <Banner tone="caution" title="Couldn't load the map" reason={mapFailed}>
          You can still set the pin by pasting its coordinates in the field above.
        </Banner>
      ) : withMap ? (
        <div className="rq-map-box">
          <Suspense
            fallback={
              <div className="rq-map-loading" role="status">
                Loading the map…
              </div>
            }
          >
            <MapCanvas
              accessToken={accessToken}
              pin={pin}
              focusKey={focusKey}
              disabled={disabled}
              label={`${label}: the map. Click to place the pin, or drag the pin to move it.`}
              onPin={place}
              onFailed={setMapFailed}
            />
          </Suspense>
        </div>
      ) : null}

      <div className="rq-map-readout" aria-live="polite">
        {pin ? (
          <>
            <span className="rq-map-pinned">
              <Icon name="map-pin" size={14} />
              <span>
                Pinned at <span className="text-mono-small">{formatCoordinates(pin)}</span>
                {lookingUp ? <span className="rq-map-looking"> · finding the address…</span> : null}
              </span>
            </span>
            <span className="rq-map-actions">
              <a className="rq-map-link" href={mapsHref(pin)} target="_blank" rel="noreferrer">
                Open in maps
                <Icon name="external-link" size={12} />
              </a>
              {disabled ? null : (
                <Button size="sm" variant="quiet" icon="x" onClick={remove}>
                  Remove pin
                </Button>
              )}
            </span>
          </>
        ) : (
          <span className="rq-missing">No pin yet</span>
        )}
      </div>

      {error ? (
        <p className="rq-field-error">
          <Icon name="octagon-alert" size={14} />
          {error}
        </p>
      ) : hint ? (
        <p className="rq-field-hint">{hint}</p>
      ) : null}
    </div>
  );
}
